import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useAuthStore } from '../stores/authStore'

export interface Permission {
  module: string
  action: string
}

export interface UserOrganizations {
  staffOrganization: any
  userOrganizations: any[]
}

export interface PermissionData {
  permissions: string[]
  organizations: UserOrganizations
}

export const usePermissions = (user?: any) => {
  const { token } = useAuthStore()
  const [permissions, setPermissions] = useState<string[]>([])
  const [organizations, setOrganizations] = useState<UserOrganizations | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Use the token from the Zustand store
      if (!token) {
        setLoading(false)
        return
      }

      const response = await axios.get('/api/permissions/my-permissions', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data.success) {
        setPermissions(response.data.data.permissions)
        setOrganizations(response.data.data.organizations)
      } else {
        setError('Failed to fetch permissions')
      }
    } catch (err: any) {
      // Silently handle errors - don't show toasts for permission fetch failures
      // These are often expected during initial load before token is ready
      const status = err.response?.status
      if (status === 401 || status === 403) {
        // Expected auth errors - silently ignore
        console.debug('Permissions fetch skipped (auth not ready):', status)
      } else {
        // Other errors - log but don't show toast
        console.error('Error fetching permissions:', err)
        setError(err.response?.data?.error || 'Failed to fetch permissions')
      }
    } finally {
      setLoading(false)
    }
  }, []) // Remove token dependency to prevent infinite loops

  useEffect(() => {
    if (user && token) {
      fetchPermissions()
    } else if (user && !token) {
      // User exists but no token yet - wait for token
    } else {
      setPermissions([])
      setOrganizations([])
      setError(null)
    }
  }, [user, token]) // Remove fetchPermissions dependency to prevent infinite loops

  const hasPermission = useCallback((module: string, action: string): boolean => {
    const permissionKey = `${module}:${action}`
    return permissions.includes(permissionKey)
  }, [permissions])

  const hasModuleAdmin = useCallback((module: string): boolean => {
    return hasPermission(module, 'admin')
  }, [hasPermission])

  const hasModuleWrite = useCallback((module: string): boolean => {
    return hasPermission(module, 'write') || hasPermission(module, 'admin')
  }, [hasPermission])

  const hasModuleRead = useCallback((module: string): boolean => {
    return hasPermission(module, 'read') || hasPermission(module, 'write') || hasPermission(module, 'admin')
  }, [hasPermission])

  const getAdminMenuItems = useCallback(() => {
    const menuItems = []

    // Workflow Configuration - requires workflow:admin
    if (hasModuleAdmin('workflow')) {
      menuItems.push({
        to: '/workflow-configuration',
        label: 'Workflow Configuration',
        permission: 'workflow:admin'
      })
    }

    // Analytics & Reporting - requires analytics:read
    if (hasModuleRead('analytics')) {
      menuItems.push({
        to: '/analytics',
        label: 'Analytics & Reporting',
        permission: 'analytics:read'
      })
    }

    // Staff Assignments - requires users:admin
    if (hasModuleAdmin('users')) {
      menuItems.push({
        to: '/staff-assignments',
        label: 'Staff Assignments',
        permission: 'users:admin'
      })
    }

    // Smart Routing - requires tickets:admin
    if (hasModuleAdmin('tickets')) {
      menuItems.push({
        to: '/smart-routing',
        label: 'Smart Routing',
        permission: 'tickets:admin'
      })
    }

    // Staff Organizations - requires organizations:read
    if (hasModuleRead('organizations')) {
      menuItems.push({
        to: '/staff-organizations',
        label: 'Staff Organizations',
        permission: 'organizations:read'
      })
    }

    // Client Organizations - requires organizations:read
    if (hasModuleRead('organizations')) {
      menuItems.push({
        to: '/client-organizations',
        label: 'Client Organizations',
        permission: 'organizations:read'
      })
    }

    // User Management - requires users:read or users:write or users:admin
    if (hasModuleRead('users')) {
      menuItems.push({
        to: '/user-management',
        label: 'User Management',
        permission: 'users:read'
      })
    }

    // Role Management - requires users:admin (only admins can manage roles)
    if (hasModuleAdmin('users')) {
      menuItems.push({
        to: '/role-management',
        label: 'Role Management',
        permission: 'users:admin'
      })
    }

    // Seed Management - requires users:admin (only admins can run seeds)
    if (hasModuleAdmin('users')) {
      menuItems.push({
        to: '/admin-wizard',
        label: 'Database Wizard',
        permission: 'users:admin'
      })
    }

    // Service Management - requires services:admin
    if (hasModuleAdmin('services')) {
      menuItems.push({
        to: '/service-management',
        label: 'Service Management',
        permission: 'services:admin'
      })
    }

    // Email Monitoring - requires email_monitoring:admin
    if (hasModuleAdmin('email_monitoring')) {
      menuItems.push({
        to: '/email-monitoring',
        label: 'Email Monitoring',
        permission: 'email_monitoring:admin'
      })
    }

    // ERP Module items removed from Operations - they're now in main navigation dropdown

    return menuItems
  }, [hasModuleAdmin, hasModuleRead, hasPermission])

  const refreshPermissions = useCallback(() => {
    fetchPermissions()
  }, [fetchPermissions])

  const canAccessAdminMenu = useCallback((): boolean => {
    // Check if user has any permissions that would show in the Operations menu
    return getAdminMenuItems().length > 0
  }, [getAdminMenuItems])

  return {
    permissions,
    organizations,
    loading,
    error,
    hasPermission,
    hasModuleAdmin,
    hasModuleWrite,
    hasModuleRead,
    canAccessAdminMenu,
    getAdminMenuItems,
    refreshPermissions
  }
}
